import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from './useApi';
import { UploadProgress, loadPersistedProgress, persistProgress, clearPersistedProgress } from './uploadProgressStorage';
import { mapPipelineStatus, isTerminalStatus } from './pipelineStatusMapper';

const PIPELINE_STATUS_POLL_INTERVAL_MS = 1_500;
const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Polls GET /storage/meter-data/pipeline-status/:runId on a fixed short interval until
 * the pipeline reaches a terminal status. Each call is a short request/response — no
 * connection is held open between polls, unlike the old SSE stream this replaced —
 * but the UI update cadence and reconnect-on-trouble messaging match it closely.
 */
export function useUploadProgress(runId: string | null) {
  const [progress, setProgress] = useState<UploadProgress>(() => {
    const persisted = loadPersistedProgress();
    return persisted?.runId === runId
      ? persisted
      : { status: 'idle', message: '', progress: 0 };
  });

  const { checkPipelineStatusOnce } = useApi();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failureCountRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (id: string) => {
    try {
      const status = await checkPipelineStatusOnce(id);
      failureCountRef.current = 0;
      const uiStatus = mapPipelineStatus(status.status);

      setProgress((prev) => ({
        ...prev,
        status: uiStatus,
        message: status.message || '',
        progress: status.progress || 0,
        recordsProcessed: status.recordsProcessed,
        runId: id,
      }));

      if (isTerminalStatus(uiStatus)) stopPolling();
    } catch {
      failureCountRef.current += 1;

      if (failureCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
        setProgress((prev) => ({
          ...prev,
          status: 'error',
          message: 'Connection lost after multiple attempts. Please refresh the page.',
        }));
        stopPolling();
      } else {
        setProgress((prev) => ({
          ...prev,
          message: `Connection lost. Reconnecting (${failureCountRef.current}/${MAX_CONSECUTIVE_FAILURES})...`,
        }));
      }
    }
  }, [checkPipelineStatusOnce, stopPolling]);

  useEffect(() => {
    if (progress.status !== 'idle' && progress.runId) persistProgress(progress);
  }, [progress]);

  useEffect(() => {
    if (!runId) {
      const persisted = loadPersistedProgress();
      if (persisted?.runId) setProgress(persisted);
      return undefined;
    }

    failureCountRef.current = 0;
    pollOnce(runId);
    pollRef.current = setInterval(() => pollOnce(runId), PIPELINE_STATUS_POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [runId, pollOnce, stopPolling]);

  // Refresh immediately when the tab regains focus, rather than waiting out the
  // rest of the current interval — browsers throttle background-tab timers, so
  // this keeps the displayed status from lagging after a long time away.
  useEffect(() => {
    if (!runId || isTerminalStatus(progress.status) || progress.status === 'idle') return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      pollOnce(runId);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [runId, progress.status, pollOnce]);

  const clearProgress = useCallback(() => {
    setProgress({ status: 'idle', message: '', progress: 0 });
    clearPersistedProgress();
    stopPolling();
  }, [stopPolling]);

  return { progress, clearProgress };
}
